import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import {
  BUSY,
  INPUT_REQUIRED,
  INVALID_REQUEST,
  OUTCOME_UNKNOWN,
  TOKEN_INVALID,
  UNAVAILABLE,
  cardActionBiEvent,
  cardActionRequest,
  checkedAct,
  checkedArgs,
  executeCardAction,
  prepareCardAction,
  refusalNotice,
  undoPrepareCardAction,
  type CardActionStage,
  type PreparedCardAction,
} from '../../../lib/card-actions';
import type { CalendarItemCard } from '../../../lib/calendar/calendar-item-card';
import type { ChatRiders } from '../../../lib/widget-chat';
import { ownerDisplayTextFor, type RecordNames } from '../../../lib/owner-display-text';
import { clearWidgetToolResults } from '../../../lib/widgets';
import { Toast } from '../ui';
import { CardActionDialog } from './CardActionDialog';
import { DashboardVisibleContext } from './dashboard-visible';

// How long a reversible write offers "Undo". The harness honours the undo token for longer, so a press
// near the end still prepares.
const UNDO_WINDOW_MS = 30_000;
// A refusal notice that offers "Ask in chat" stays long enough to read it and decide.
const ASK_WINDOW_MS = 10_000;
const PREPARE_LOAD = 'card-action-prepare';

/** A frame's `{kind:'act'}` message, after the host checked it came from its own frame. */
export interface CardActMessage {
  widget: string;
  /** The drill-in page an act from outside every card slot names; '' (or absent) for a card's act. */
  page?: string;
  op: string;
  record: string;
  version: number;
  /** The card's own chat prompt with its row's ids filled in, used when the harness refuses without one. */
  fallback: string;
  /** The row's booking card, sent with the fallback so the owner's bubble shows the booking, not its id. */
  fallbackCalendarItem?: CalendarItemCard;
  /** The names of the ids in the fallback prompt, for the owner's bubble; matched against whichever prompt is really sent. */
  fallbackNames?: RecordNames;
  /** The owner's answers to the op's form, as the frame sent them; `checkedArgs` decides what may go to prepare. */
  args?: unknown;
  /** Names this press, so the form INPUT_REQUIRED asks for is drawn in the row that pressed. An older card sends none. */
  rid?: string;
}

/** What rides with `prompt`, the fallback really sent: the harness's own when it names one, else the frame's. */
function fallbackRiders({ fallbackCalendarItem, fallbackNames }: CardActMessage, prompt: string): ChatRiders {
  const displayText = ownerDisplayTextFor(prompt, fallbackNames);
  return {
    ...(fallbackCalendarItem ? { calendarItem: fallbackCalendarItem } : {}),
    ...(displayText ? { displayText } : {}),
  };
}

/** The host's way into the frame it rendered: the payload goes in the bridge envelope, as a confirm's answer does. */
export type PostToFrame = (payload: object) => void;

/** `prompt` is where a refusal sends the owner: the harness's fallback, else the frame's. */
type Open = { prepared: PreparedCardAction; op: string; prompt: string; riders?: ChatRiders };
/** `ask` is what a refusal notice's "Ask in chat" sends: the fallback prompt, with the row's booking card and words. */
type Notice = {
  message: string;
  severity: 'success' | 'error';
  undo?: { token: string; op: string };
  ask?: { prompt: string; riders: ChatRiders };
};

/**
 * A dashboard host's half of a card's direct write: prepare with the harness, the app's own confirm,
 * execute, and Undo. Everything the harness returns stays here: the host never hands any of it to the
 * frame, which only ever sent the request.
 *
 * One at a time per host: an `act` is refused while another is being prepared, confirmed or executed, or
 * while a widget's own confirm is open, and a widget confirm is refused (answered "no") while an act is
 * under way. Two dialogs on one screen would let a card's confirm stand in for the app's.
 *
 * Only while its dashboard is on screen: the host keeps a dashboard it left mounted, hidden, and the dialog,
 * the notice and a widget's confirm draw over the whole page. Leaving cancels an open dialog or widget
 * confirm and drops the notice; a prepare that answers after that is dropped, and a hidden frame's act or
 * confirm is refused.
 *
 * `dashboardSlug` is the one the host rendered: the card's widget is resolved inside it, never from the frame.
 * `onFallback` is the host's own chat hand-off, the one a widget prompt uses: a refusal that wrote nothing sends
 * the card's fallback prompt there, with the row's booking card and words when the frame sent them, so the button
 * still does what it did before cards could act. An undo, or an execute whose outcome is unknown, never does: that
 * prompt asks for the write itself. A prepare refusal about the record itself (`refusalNotice`) shows a notice instead,
 * and goes to chat only when the owner taps "Ask in chat".
 * `onWritten` is the host's own reload, run after a write so the card shows the record as it now is; the
 * widgets' cached results are dropped first, or the reload would paint the old row again.
 *
 * The one thing that does go back into the frame is the form an op asks for (INPUT_REQUIRED): its fields go to the row
 * whose press carried the `rid`, which sends the act again with the owner's answers in `args`. A refusal that names
 * no code the app knows, but carries the harness's `notice`, shows that notice rather than opening chat unasked.
 */
export function useCardAction({
  dashboardSlug,
  onFallback,
  onWritten,
}: {
  dashboardSlug: string;
  onFallback: (prompt: string, riders?: ChatRiders) => void;
  onWritten: () => void;
}) {
  const { t } = useTranslation();
  const { logItemSeen, logClickCta, logPageClickBi, logLoadStart, logLoadFinish } = useGenericBi();
  const [dialog, setDialog] = useState<Open | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  // Refs, not state, because the hosts' message listeners are bound once and must see the current answer.
  const busyRef = useRef(false);
  const dialogRef = useRef<Open | null>(null);
  // The initial prepare waiting for its answer. Hiding the dashboard drops it, and an answer for one that is
  // no longer here is ignored. An Undo press is already confirmed, so its prepare and execute are not dropped.
  const preparingRef = useRef<{ op: string } | null>(null);
  const visible = useContext(DashboardVisibleContext);
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  // The widget confirm on screen, if any. Only that one's settling clears it: a newer confirm replaces an
  // older one, which settles first.
  const widgetConfirmAbortRef = useRef<AbortController | null>(null);
  const latest = useRef({ dashboardSlug, onFallback, onWritten, t, logItemSeen, logClickCta, logPageClickBi, logLoadStart, logLoadFinish });
  latest.current = { dashboardSlug, onFallback, onWritten, t, logItemSeen, logClickCta, logPageClickBi, logLoadStart, logLoadFinish };

  // Prepare reads are loads: the initial one gates the dialog, and undo-prepare gates the direct inverse.
  const loadDialog = useCallback((read: Promise<PreparedCardAction>): Promise<PreparedCardAction> => {
    const started = Date.now();
    latest.current.logLoadStart(PREPARE_LOAD);
    return read.then((prepared) => {
      const isSuccessful = !(prepared.refused && prepared.reason === UNAVAILABLE);
      latest.current.logLoadFinish(PREPARE_LOAD, { isSuccessful, loadingTimeMs: Date.now() - started });
      return prepared;
    });
  }, []);

  const bi = useCallback((stage: CardActionStage, op: string, outcome: string, durationMs?: number) => {
    const ev = cardActionBiEvent(stage, op, outcome, durationMs);
    const { logItemSeen: seen, logClickCta: click } = latest.current;
    (ev.type === 'itemSeen' ? seen : click)(ev.componentName, { additionalFields: ev.additionalFields });
  }, []);

  const refuse = useCallback(
    (op: string, reason: string, prompt: string, durationMs?: number, riders?: ChatRiders) => {
      busyRef.current = false;
      bi('refused', op, reason, durationMs);
      if (prompt) latest.current.onFallback(prompt, riders);
      else setNotice({ message: latest.current.t('cardAction.failed'), severity: 'error' });
    },
    [bi],
  );

  const present = useCallback(
    (
      prepared: PreparedCardAction,
      op: string,
      message: CardActMessage,
      durationMs: number,
      press: { rid: string; postToFrame: PostToFrame },
    ) => {
      const prompt = prepared.fallbackPrompt || message.fallback;
      const riders = fallbackRiders(message, prompt);
      if (prepared.refused) {
        const reason = prepared.reason || UNAVAILABLE;
        if (reason === INPUT_REQUIRED && prepared.inputFields.length > 0 && press.rid) {
          // Not a failure, so no `refused` event: the owner answers in the row, and Submit is a new act that logs its own.
          busyRef.current = false;
          press.postToFrame({ kind: 'input', rid: press.rid, fields: prepared.inputFields, notice: prepared.notice });
          return;
        }
        const known = refusalNotice(reason);
        if (known || prepared.notice) {
          busyRef.current = false;
          bi('refused', op, reason, durationMs);
          setNotice({
            message: known ? latest.current.t(known.messageKey) : prepared.notice,
            severity: 'error',
            ask: (known ? known.asksInChat : true) && prompt ? { prompt, riders } : undefined,
          });
          return;
        }
        refuse(op, reason, prompt, durationMs, riders);
        return;
      }
      bi('shown', op, 'do', durationMs);
      dialogRef.current = { prepared, op, prompt, riders };
      setDialog(dialogRef.current);
    },
    [bi, refuse],
  );

  const act = useCallback(
    (message: CardActMessage, postToFrame: PostToFrame) => {
      const { op, record, valid } = checkedAct(message.op, message.record);
      if (busyRef.current || widgetConfirmAbortRef.current || !visibleRef.current) {
        bi('refused', op, BUSY);
        return;
      }
      const args = checkedArgs(message.args);
      if (!valid || !args) {
        refuse(op, INVALID_REQUEST, message.fallback, undefined, fallbackRiders(message, message.fallback));
        return;
      }
      busyRef.current = true;
      const startedAt = Date.now();
      const pending = { op };
      preparingRef.current = pending;
      void loadDialog(
        prepareCardAction(
          cardActionRequest(latest.current.dashboardSlug, { widget: message.widget, page: message.page, op, record, version: message.version }, args),
        ),
      ).then((prepared) => {
        if (preparingRef.current !== pending) return;
        preparingRef.current = null;
        present(prepared, op, message, Date.now() - startedAt, {
          rid: message.rid ?? '',
          postToFrame,
        });
      });
    },
    [bi, loadDialog, present, refuse],
  );

  const execute = useCallback(
    (open: Open, undo: boolean, startedAt: number, skipCustomerNotification = false) => {
      void executeCardAction(open.prepared.token, { skipCustomerNotification }).then((done) => {
        busyRef.current = false;
        const durationMs = Date.now() - startedAt;
        const translate = latest.current.t;
        if (done.reason === OUTCOME_UNKNOWN) {
          bi('refused', open.op, done.reason, durationMs);
          setNotice({ message: translate('cardAction.outcomeUnknown'), severity: 'error' });
          return;
        }
        if (!done.ok) {
          // Every other failure was refused before Wix wrote anything.
          refuse(open.op, done.reason, open.prompt, durationMs, open.riders);
          return;
        }
        bi(undo ? 'undone' : 'done', open.op, 'ok', durationMs);
        clearWidgetToolResults();
        latest.current.onWritten();
        setNotice(
          undo
            ? { message: translate('cardAction.undone'), severity: 'success' }
            : {
                message: translate('cardAction.done'),
                severity: 'success',
                undo: done.undoToken ? { token: done.undoToken, op: open.op } : undefined,
              },
        );
      });
    },
    [bi, refuse],
  );

  const answer = useCallback(
    (ok: boolean, { notifyCustomer }: { notifyCustomer: boolean }) => {
      const open = dialogRef.current;
      if (!open) return;
      dialogRef.current = null;
      setDialog(null);
      if (!ok) {
        busyRef.current = false;
        bi('cancelled', open.op, 'do');
        return;
      }
      bi('confirmed', open.op, 'do');
      // The dialog answers `notifyCustomer: true` unless it offered the switch and the owner turned it off.
      execute(open, false, Date.now(), !notifyCustomer);
    },
    [bi, execute],
  );

  const undo = useCallback(() => {
    const offer = notice?.undo;
    if (!offer || busyRef.current || widgetConfirmAbortRef.current) return;
    latest.current.logPageClickBi({ page_name: 'dashboard', button_name: 'card_action_undo' });
    busyRef.current = true;
    setNotice(null);
    const startedAt = Date.now();
    bi('confirmed', offer.op, 'undo');
    void loadDialog(undoPrepareCardAction(offer.token)).then((prepared) => {
      if (prepared.refused) {
        if (prepared.reason !== TOKEN_INVALID) {
          // The token is still good: keep offering Undo, so a passing failure can be tried again.
          busyRef.current = false;
          bi('refused', offer.op, prepared.reason || UNAVAILABLE, Date.now() - startedAt);
          // Hiding the dashboard dropped the offer with its notice; a refusal answering later does not bring it back.
          if (visibleRef.current) setNotice({ message: latest.current.t('cardAction.failed'), severity: 'error', undo: offer });
          return;
        }
        refuse(offer.op, prepared.reason, '', Date.now() - startedAt);
        return;
      }
      execute({ prepared, op: offer.op, prompt: '' }, true, startedAt);
    });
  }, [notice, bi, execute, loadDialog, refuse]);

  /** Runs a widget's `sym.confirm` unless an act is under way or the dashboard is hidden, which answer it "no"
   *  without asking. `ask` gets the signal that withdraws the confirm when the dashboard is hidden. */
  const guardWidgetConfirm = useCallback((ask: (signal: AbortSignal) => Promise<boolean>): Promise<boolean> => {
    if (busyRef.current || !visibleRef.current) return Promise.resolve(false);
    const abort = new AbortController();
    widgetConfirmAbortRef.current = abort;
    return ask(abort.signal).finally(() => {
      if (widgetConfirmAbortRef.current === abort) widgetConfirmAbortRef.current = null;
    });
  }, []);

  const askInChat = useCallback(() => {
    const ask = notice?.ask;
    if (!ask) return;
    latest.current.logPageClickBi({ page_name: 'dashboard', button_name: 'card_action_ask_in_chat' });
    setNotice(null);
    latest.current.onFallback(ask.prompt, ask.riders);
  }, [notice]);

  const closeNotice = useCallback(() => setNotice(null), []);

  useEffect(() => {
    if (visible) return;
    const preparing = preparingRef.current;
    if (preparing) {
      preparingRef.current = null;
      busyRef.current = false;
      bi('cancelled', preparing.op, 'do');
    }
    const open = dialogRef.current;
    if (open) {
      dialogRef.current = null;
      setDialog(null);
      busyRef.current = false;
      bi('cancelled', open.op, 'do');
    }
    widgetConfirmAbortRef.current?.abort();
    setNotice(null);
  }, [visible, bi]);

  const element = (
    <>
      {dialog && <CardActionDialog key={dialog.prepared.token} prepared={dialog.prepared} onAnswer={answer} />}
      <Toast
        open={visible && notice != null}
        message={notice?.message ?? ''}
        severity={notice?.severity}
        autoHideDuration={notice?.undo ? UNDO_WINDOW_MS : notice?.ask ? ASK_WINDOW_MS : undefined}
        action={
          notice?.undo
            ? { label: t('cardAction.undo'), onClick: undo }
            : notice?.ask
              ? { label: t('cardAction.askInChat'), onClick: askInChat }
              : undefined
        }
        onClose={closeNotice}
      />
    </>
  );

  return { act, guardWidgetConfirm, element };
}
